import type { ISODateTimeString, UUID } from './shared';

export type SpaceType = 'community' | 'venue' | 'event';
export type SpaceStatus = 'active' | 'inactive' | 'archived';
/**
 * Membership roles on a Space. Defined as a const-object + derived type so
 * both the runtime value and the TypeScript type share one source of
 * truth. Mirrors the backend `MemberRoleType` enum in
 * `app/db/models/space_member.py` — keep in sync if the backend grows a
 * new role. Same pattern as `EventImportPlatform` in `./event.ts`.
 */
export const SpaceMemberRole = {
  admin: 'admin',
  moderator: 'moderator',
  member: 'member',
} as const;
export type SpaceMemberRole = (typeof SpaceMemberRole)[keyof typeof SpaceMemberRole];
/**
 * Membership status on a Space. Defined as a const-object + derived type
 * so both the runtime value and the TypeScript type share one source of
 * truth. Mirror of the backend `MemberStatusType` enum — keep in sync if
 * the backend grows a new status. Same pattern as `SpaceMemberRole` above.
 */
export const SpaceMemberStatus = {
  approved: 'approved',
  pending: 'pending',
  declined: 'declined',
  blocked: 'blocked',
} as const;
export type SpaceMemberStatus = (typeof SpaceMemberStatus)[keyof typeof SpaceMemberStatus];

/**
 * Status values an admin can transition a member to via the moderation
 * actions on `MemberDetailDrawer` / `MemberCard`. Excludes `pending`
 * because pending → pending isn't a meaningful action.
 */
export type MemberAdminAction = Exclude<SpaceMemberStatus, 'pending'>;

/**
 * `status` value of the members list (GET /spaces/{id}/members) for the admin All chip:
 * approved + pending, approved first (backend `ALL_OPEN_STATUS`). Admins and moderators only.
 */
export const MEMBERS_LIST_ALL_OPEN = 'all_open';

export interface SpaceUserSummary {
  id: UUID;
  full_name: string | null;
  slug: string | null;
  profile_picture: string | null;
}

/**
 * Loose mirror of the backend `Space.activation_progress` JSON column. The
 * backend stores activation progress as deliberately schema-light freeform
 * JSON (`{ version, completed: { "<sub_step_id>": { at, refs? } } }`), so every
 * field is optional and `completed` entries may be `null`. Counter sub-steps
 * (e.g. `setup.seed_posts`) additionally carry a `refs` map keyed by the
 * contributing entity id (the created question), so repeat completions
 * accumulate toward a threshold instead of flipping a single flag. The FE validates
 * and coerces this into the strict `ActivationProgress` type at the boundary
 * via `parseActivationProgress` in `@lib/spaceActivation` — do not consume
 * this raw shape directly in feature code.
 */
export interface ActivationProgressRaw {
  version?: number;
  completed?: Record<string, { at?: string; refs?: Record<string, { at?: string }> } | null>;
}

/** Enriched space response matching backend SpaceRead with computed fields. */
export interface CommunityRead {
  id: UUID;
  name: string;
  bio: string | null;
  description: string | null;
  type: SpaceType;
  status: SpaceStatus;
  image_url: string | null;
  banner_image_url: string | null;
  access_code: string | null;
  has_access_code: boolean;
  /** Whether the Space's join PIN is currently active. Server-owned, split from
   * the secret value so a remembered-but-disabled PIN can exist without leaking. */
  pin_enabled: boolean;
  is_private: boolean;
  auto_approve: boolean;
  is_unlisted: boolean;
  /** Whether a Meetball platform admin has featured this space on the
   * discovery "Featured" rail. Server-owned; toggled only via the
   * platform-admin `PATCH /spaces/{identifier}/featured` endpoint. */
  is_featured: boolean;
  tags: string[];
  topics: string[];
  show_member_list: boolean;
  allow_messaging: boolean;
  invite_token: string | null;
  slug: string;
  creator_id: UUID;
  created_at: ISODateTimeString;
  updated_at: ISODateTimeString;

  // Computed fields
  member_count: number;
  event_count: number;
  question_count: number;
  user_is_member: boolean;
  user_role: SpaceMemberRole | null;
  user_membership_status: SpaceMemberStatus | null;
  creator: SpaceUserSummary | null;

  // Per-space owner activation progress. Freeform backend JSON; validate and
  // coerce via `parseActivationProgress` before consuming.
  activation_progress?: ActivationProgressRaw | null;
  // TECH-86 partner numbers for the requesting viewer (`PartnerCounts`). Only the Space
  // DETAIL response (backend SpaceDetailRead) carries them; list rows never do, hence optional.
  partners_visible_count?: number;
  members_only_perk_count?: number;
  members_only_sponsor_count?: number;
  partners_has_value?: boolean;
}

export interface CommunityCreate {
  name: string;
  bio?: string | null;
  description?: string | null;
  type: SpaceType;
  image_url?: string | null;
  is_private?: boolean;
  auto_approve?: boolean;
  is_unlisted?: boolean;
  tags?: string[];
  topics?: string[];
  slug?: string | null;
  referral_code?: string | null;
}

export interface SpaceMemberRead {
  id: UUID;
  space_id: UUID;
  user_id: UUID;
  role: SpaceMemberRole;
  status: SpaceMemberStatus;
  joined_at: ISODateTimeString;
  user: SpaceUserSummary | null;
  /**
   * Contextual identity in THIS space — "Founder of Meetball", "Father of
   * Samuel". Display only: `role` remains the sole permission signal, and the
   * two render side by side because they answer different questions.
   *
   * Optional on the type because older backends omit the field entirely;
   * `null` means "member has no hat", which renders as nothing for other
   * members and as a placeholder on the viewer's own row.
   */
  hat?: string | null;
  /**
   * Whether this request arrived with the Space's correct PIN. Filled only for
   * the Space's creator and approved admins; `null` for everyone else,
   * moderators included. Optional because older backends omit it.
   */
  pin_verified?: boolean | null;
}

/**
 * `meta` of GET /spaces/{identifier}/members. Non-null only for the creator, approved admins and
 * moderators; a moderator's carries `status_counts` only.
 */
export interface SpaceMembersListMeta {
  status_counts?: Record<string, number>;
  /** Pending requests that carry the correct PIN, as of `as_of`. Creator and approved admins only. */
  pin_verified_pending?: number;
  /**
   * Opaque server timestamp; echoed back to bulk-approve. Only `Date.parse` is safe. Creator and
   * approved admins only.
   */
  as_of?: string;
}

/** The most pending requests one bulk approve lets in: mirrors the backend's `BULK_APPROVE_LIMIT`. */
export const BULK_APPROVE_LIMIT = 200;

/** Body of POST /spaces/{identifier}/members/bulk-approve. */
export interface BulkApproveMembersBody {
  /** `meta.as_of` of the members list the host decided on; later requests are not approved. */
  as_of: string;
  /** Only the requests that carry a recorded correct PIN; omitted, every pending request. */
  pin_verified?: true;
}

/** Response of POST /spaces/{identifier}/members/bulk-approve. */
export interface BulkApproveMembersResult {
  approved_user_ids: string[];
}

/** Partial update payload for PUT /spaces/{identifier}. All fields optional. */
export interface CommunityUpdate {
  name?: string;
  bio?: string | null;
  description?: string | null;
  image_url?: string | null;
  banner_image_url?: string | null;
  is_private?: boolean;
  auto_approve?: boolean;
  is_unlisted?: boolean;
  access_code?: string | null;
  /** Server owns the PIN value; the client only toggles enabled-state. */
  pin_enabled?: boolean;
  tags?: string[];
  topics?: string[];
  show_member_list?: boolean;
  allow_messaging?: boolean;
}

export interface SpacePageRead {
  id: UUID;
  space_id: UUID;
  type: string;
  data: Record<string, unknown>;
  is_enabled: boolean;
  created_at: ISODateTimeString;
  updated_at: ISODateTimeString;
}

export interface SpacePageUpdate {
  data?: Record<string, unknown>;
  is_enabled?: boolean;
}

export interface SpaceCategory {
  id: UUID;
  name: string;
  display_order: number;
  is_active: boolean;
}
